import React from "react";
import { Button, Card, Modal, Space, Tag, Typography } from "antd";
import { RiseOutlined } from "@ant-design/icons";
import {
  useGetKinderGraduationStatusQuery,
  useGraduateKinderStudentsMutation,
} from "../../../../store/api/schoolsApi";
import { useNotification } from "../../../../hooks/useNotification";
import { KinderGraduationResult } from "../../../../services/schools/school.types";

const { Text, Title } = Typography;

interface KinderGraduationCardProps {
  schoolId: number;
}

const buildSummary = (result: KinderGraduationResult): string => {
  const base = `${result.promoted_to_k2} alumnos pasaron a K2 · ${result.promoted_to_k3} pasaron a K3 · ${result.graduated_from_k3} alumnos egresaron`;

  // Surfaced instead of swallowed: these students did move up a grade, but the
  // destination grade has no group with their name, so an admin has to place
  // them by hand.
  return result.without_destination_classroom > 0
    ? `${base}. ${result.without_destination_classroom} quedaron sin clase porque su grupo no existe en el grado destino.`
    : base;
};

/**
 * Bulk Kinder graduation (SCRUM-721), rendered at the bottom of the Escuela tab.
 *
 * The card only appears for schools that actually have Kinder students, and the
 * action locks itself once it ran for the current school cycle — promoting the
 * same students twice would advance them two grades with no way back.
 */
const KinderGraduationCard: React.FC<KinderGraduationCardProps> = ({
  schoolId,
}) => {
  const { data: status, isLoading } =
    useGetKinderGraduationStatusQuery(schoolId);
  const [graduateKinderStudents, { isLoading: isGraduating }] =
    useGraduateKinderStudentsMutation();
  const { notify } = useNotification();
  // Context-based Modal API: AntD v5 static Modal.confirm does not render
  // under React 19.
  const [modal, modalContextHolder] = Modal.useModal();

  if (isLoading || !status?.has_kinder_students) {
    return null;
  }

  const alreadyGraduated = status.already_graduated_this_cycle;

  const runGraduation = async () => {
    try {
      const result = await graduateKinderStudents(schoolId).unwrap();
      notify("success", "Alumnos graduados correctamente", buildSummary(result));
    } catch (error: any) {
      const backendMessage = error?.data?.message;
      notify(
        "error",
        "Error",
        backendMessage
          ? `No se pudieron graduar los alumnos.\n${backendMessage}`
          : "No se pudieron graduar los alumnos. Reintentá en unos segundos."
      );
    }
  };

  const confirmGraduation = () => {
    modal.confirm({
      title: "¿Graduar alumnos de Kinder?",
      content: (
        <div>
          <p>Se actualizarán todos los alumnos de la escuela:</p>
          <ul>
            <li>K1 → K2</li>
            <li>K2 → K3</li>
            <li>K3 → egresados de la escuela</li>
          </ul>
          <p>
            Los datos históricos de los alumnos de K3 se conservarán.
          </p>
          <p>
            <strong>Esta acción no puede deshacerse automáticamente.</strong>
          </p>
        </div>
      ),
      okText: "Sí, graduar alumnos",
      okButtonProps: { danger: true },
      cancelText: "Cancelar",
      onOk: runGraduation,
    });
  };

  return (
    <Card style={{ marginTop: 24 }}>
      {modalContextHolder}
      <div className="school-status-card">
        <Space align="start" size={16}>
          <RiseOutlined style={{ fontSize: 24 }} aria-hidden="true" />
          <div>
            <Title level={5} style={{ marginBottom: 8 }}>
              Graduación de Kinder
            </Title>
            <Tag color={alreadyGraduated ? "green" : "blue"}>
              {alreadyGraduated
                ? `Ya se graduó en el ciclo ${status.ciclo}`
                : `Ciclo ${status.ciclo}`}
            </Tag>
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">
                {`K1: ${status.students_by_grade.k1} · K2: ${status.students_by_grade.k2} · K3: ${status.students_by_grade.k3} alumnos`}
              </Text>
            </div>
          </div>
        </Space>

        <div className="school-status-card__action">
          <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
            {alreadyGraduated
              ? "Los alumnos de Kinder de esta escuela ya fueron graduados en este ciclo escolar. La acción vuelve a habilitarse cuando la escuela pase al siguiente ciclo."
              : "Al graduar, los alumnos de K1 pasan a K2, los de K2 pasan a K3 y los de K3 egresan de la escuela conservando todo su historial."}
          </Text>
          <Button
            danger
            icon={<RiseOutlined />}
            disabled={alreadyGraduated}
            loading={isGraduating}
            onClick={confirmGraduation}
          >
            Graduar alumnos de Kinder
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default KinderGraduationCard;
